import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, FileText, Building2, Phone, MessageCircle, Scale, Download } from "lucide-react";
import api from "@/lib/api";
import BrandMark from "@/components/BrandMark";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Terms() {
  const { t, i18n } = useTranslation();
  const isEN = i18n.language === "en";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setData(null);
      setErr(null);
      try {
        const r = await api.get("/legal/terms", { headers: { "Accept-Language": isEN ? "en" : "it" } });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [isEN]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 px-5 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <BrandMark size="md" />
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/" className="text-xs text-zinc-400 hover:text-[#FFD700] inline-flex items-center gap-1" data-testid="terms-back">
              <ArrowLeft className="w-4 h-4" /> {t("common.back")}
            </Link>
          </div>
        </div>

        <div className="flex items-center gap-2 text-[#FFD700] mb-2">
          <FileText className="w-4 h-4" />
          <p className="text-xs uppercase tracking-[0.2em]">{isEN ? "Legal" : "Documento legale"}</p>
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tight" data-testid="terms-title">
          {data?.title || (isEN ? "General Vehicle Rental Terms" : "Condizioni Generali di Noleggio Veicoli")}
        </h1>
        <p className="text-zinc-500 mt-2 text-sm">
          {isEN ? "Last updated:" : "Ultimo aggiornamento:"} {data?.updated_at || "—"}
        </p>

        {/* Intro callout */}
        {data?.intro && (
          <div className="mt-6 rounded-xl border border-[#FFD700]/30 bg-[#FFD700]/5 p-4 text-sm text-zinc-300 leading-relaxed">
            {data.intro}
          </div>
        )}

        {/* Company block */}
        {data?.company && (
          <div className="mt-6 rounded-xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="terms-company">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 mb-3">
              <Building2 className="w-3.5 h-3.5" /> {isEN ? "Lessor (Rent Lupi)" : "Locatore (Rent Lupi)"}
            </div>
            <p className="text-zinc-100 font-semibold">{data.company.legal_name}</p>
            <p className="text-zinc-400 text-sm">{data.company.trade_name}</p>
            <p className="text-zinc-300 text-sm mt-1">{data.company.address_line1}</p>
            <p className="text-zinc-300 text-sm">{data.company.address_line2}</p>
            <p className="text-zinc-500 text-xs mt-3">
              <span className="text-zinc-600">{isEN ? "Tax code" : "Codice Fiscale"}:</span>{" "}
              <span className="font-mono text-zinc-300">{data.company.fiscal_code}</span>
              <br />
              <span className="text-zinc-600">{isEN ? "VAT" : "Partita IVA"}:</span>{" "}
              <span className="font-mono text-zinc-300">{data.company.vat}</span>
            </p>
            <div className="flex items-center gap-3 mt-3 text-sm">
              <a href={`tel:${data.company.phone}`} className="inline-flex items-center gap-1 text-zinc-300 hover:text-[#FFD700]">
                <Phone className="w-3.5 h-3.5" /> {data.company.phone}
              </a>
              <a href={`https://wa.me/+39${data.company.whatsapp.replace(/\s/g, "")}`} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 text-[#25D366] hover:brightness-110">
                <MessageCircle className="w-3.5 h-3.5" /> {data.company.whatsapp}
              </a>
            </div>
          </div>
        )}

        {/* Sections */}
        <div className="mt-8 space-y-5 text-zinc-300 text-sm leading-relaxed" data-testid="terms-body">
          {err && <p className="text-red-400">{err}</p>}
          {data?.sections?.map((s, i) => (
            <section key={i} className="border-b border-zinc-900 pb-5 last:border-0">
              <h2 className="text-zinc-100 font-semibold text-base mb-2">{s.title}</h2>
              <p>{s.body}</p>
            </section>
          ))}
          {!data && !err && (
            <p className="text-zinc-500">{t("common.loading")}</p>
          )}
        </div>

        {/* Jurisdiction badge */}
        {data?.company?.court && (
          <div className="mt-8 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 flex items-center gap-3 text-sm">
            <Scale className="w-4 h-4 text-[#FFD700]" />
            <span className="text-zinc-300">
              {isEN ? "Jurisdiction: Court of " : "Foro competente: Tribunale di "}
              <span className="font-semibold text-zinc-100">{data.company.court}</span>
            </span>
          </div>
        )}

        {/* CTA to signed PDF */}
        <div className="mt-6 rounded-xl border border-zinc-800 p-4 flex items-center justify-between gap-3">
          <p className="text-xs text-zinc-500">
            {isEN
              ? "If you have already accepted the contract, you can download your signed PDF copy from your profile."
              : "Se hai già accettato il contratto, puoi scaricare la tua copia firmata dal profilo."}
          </p>
          <Link to="/profile" className="inline-flex items-center gap-1.5 text-xs text-[#FFD700] hover:underline shrink-0">
            <Download className="w-3.5 h-3.5" /> {isEN ? "My profile" : "Il mio profilo"}
          </Link>
        </div>
      </div>
    </div>
  );
}
